/**
 * Pasos de la ayuda con Gavi («ojo de buey»): qué enseña cada uno y en qué
 * estado tiene que estar la pantalla (brújula abierta, varios elegidos…).
 */

/** Estado de la pantalla que necesita un paso. */
export type Escena =
  | 'normal'
  | 'brujula'
  | 'seleccion'
  | 'editar'
  | 'teclado'
  | 'mover'
  // Inicio: personalizando, con el selector de tamaño o con el catálogo.
  | 'inicio-editar'
  | 'inicio-tamano'
  | 'inicio-catalogo'
  // Cuentas: «Ver evolución» desplegado, el menú ⋯ de una cuenta abierto o
  // la hoja «Añadir cuenta» abierta.
  | 'patrimonio'
  | 'menu-cuenta'
  | 'nueva-cuenta'
  // Ajustes: una fila desplegada (solo estas; nunca las que cambian algo al
  // abrirlas o llevan un botón sin confirmación, como Cerrar sesión).
  | 'ajuste-amarre'
  | 'ajuste-notificaciones'
  | 'ajuste-copia'
  | 'ajuste-repuesto'
  | 'ajuste-compartir'
  | 'ajuste-tema'
  | 'ajuste-tamano'
  // Asignar el mes: la hoja «Más opciones…» abierta, el teclado de la primera
  // categoría abierto o la barra «Guardar presupuesto» a la vista (de verdad
  // si hay cambios; si no, dibujada solo para enseñarla).
  | 'asignar-mas'
  | 'asignar-teclado'
  | 'asignar-guardar'
  // Por revisar: una casilla marcada para que salga la barra de abajo.
  | 'revisar-seleccion'
  // Pagos fijos: la pestaña «Mensuales» o «Anuales» a la vista.
  | 'pagos-mensuales'
  | 'pagos-anuales'
  // Ficha de una categoría: una vista de «Evolución», «Ver movimientos»
  // desplegado o el editor del objetivo abierto (con sus pestañas de tipo
  // pasando solas, o en «Una vez»). Se cierra con «Cancelar»: nunca guarda.
  | 'ficha-gasto'
  | 'ficha-saldo'
  | 'ficha-meses'
  | 'ficha-movimientos'
  | 'ficha-editor'
  | 'ficha-editor-tipos'
  | 'ficha-editor-una-vez'
  // La pone la pantalla con `preparar` (Ahorros: pestañas y hojas de
  // ejemplo). Las escenas de arriba no se tocan.
  | 'propia';

export type EtiquetaAyuda = {
  el: Element;
  texto: string;
  /** Debajo del elemento en vez de encima (para no tapar otra etiqueta). */
  debajo?: boolean;
};

export type PasoAyuda = {
  id: string;
  escena: Escena;
  texto: string;
  /**
   * Elementos que se enfocan (se unen en un solo hueco). Se llama ya en su
   * escena; sin elementos, el paso no aplica y se salta.
   */
  objetivo: () => Element[];
  /** ¿Puede salir? Se mira al abrir la ayuda, con la pantalla normal. */
  disponible?: () => boolean;
  /** Rótulos de latón sobre partes del foco (paso «una fila»). */
  etiquetas?: () => EtiquetaAyuda[];
  /** Esquinas del hueco. */
  radio?: number;
  /** Espera a que se toque la brújula de verdad (el hueco deja pasar el dedo). */
  toque?: boolean;
  /** Último paso: Gavi aplaude y el botón dice «Entendido». */
  final?: boolean;
  /** Enseña la pestaña Dinero deslizándose arriba y abajo (sin filtrar). */
  deslizar?: boolean;
  /**
   * Botón que Gavi «pulsa» antes del paso (al avanzar): un dedo lo toca y
   * luego `preparar` abre lo que abriría. Gavi nunca pulsa nada de verdad.
   */
  pulsa?: () => Element | null;
  /** Escena `propia`: pone la pantalla como la necesita el paso. */
  preparar?: () => Promise<void>;
};

/**
 * Una entrada del menú «¿Cómo puedo ayudarte?» (Vadik, 26-oct-2026): la
 * primera es el recorrido entero de la pantalla; las demás, preguntas con
 * solo sus pasos.
 */
export type PreguntaAyuda = {
  id: string;
  texto: string;
  pasos: PasoAyuda[];
  /**
   * En vez de pasos fijos, «toca la que quieras»: los elementos laten con
   * una chapa «?» y, al tocar uno, Gavi lo explica (Inicio: «Sobre una de
   * mis tarjetas»).
   */
  elegir?: {
    objetivos: () => Element[];
    texto: string;
    explicar: (el: Element) => string;
  };
};

/** ¿Sale la pregunta? Si alguno de sus pasos puede salir ahora mismo. */
export function preguntaDisponible(pregunta: PreguntaAyuda): boolean {
  if (pregunta.elegir) {
    return pregunta.elegir.objetivos().length > 0;
  }
  return pregunta.pasos.some(
    p =>
      (p.disponible?.() ?? true) &&
      (p.escena !== 'normal' || p.objetivo().length > 0),
  );
}

/**
 * Índice del paso al que se va desde `actual` en la dirección `paso` (+1 o
 * −1), saltando los que no aplican. `fuera` (fuera de la lista) si no hay
 * ninguno: hacia delante cierra la ayuda; hacia atrás, se queda.
 */
export function siguientePaso(
  aplica: readonly boolean[],
  actual: number,
  paso: 1 | -1,
): number | 'fuera' {
  for (let i = actual + paso; i >= 0 && i < aplica.length; i += paso) {
    if (aplica[i]) {
      return i;
    }
  }
  return 'fuera';
}

/** «3 de 11»: posición entre los pasos que aplican. */
export function posicionPaso(
  aplica: readonly boolean[],
  actual: number,
): { numero: number; total: number } {
  return {
    numero: aplica.slice(0, actual + 1).filter(Boolean).length,
    total: aplica.filter(Boolean).length,
  };
}
